// Fehler.js
// Funktrainer — Fehler in einen richtigen Funkverkehr einbauen.
// Gebraucht von den Uebungsarten der Fehlersuche. Hier wird nichts geprueft;
// pruefen tut jede Ansicht selbst, weil jede etwas anderes fragt.
//
// Zwei Fehlerarten, beide mit einem Regelsatz aus daten.js begruendet:
//
//   ersatz:     ein Ablenker steht anstelle eines richtigen Bausteins.
//               Welchen er ersetzt, sagt sein Feld "ersetzt" (eine Rolle).
//               Ablenker ohne "ersetzt" werden hier nicht gebraucht.
//   vertauscht: zwei benachbarte Bausteine stehen verkehrt herum. Beide
//               Regeln gelten als Begruendung — welcher der beiden "falsch"
//               liegt, ist bei einem Tausch nicht zu entscheiden.
//
// "Fehlt" (ein Baustein fehlt) gibt es noch nicht: die Regelsaetze der Slots
// sagen, wo ein Baustein steht, nicht dass er da sein muss. Dafuer braeuchte
// es eigene Saetze.

import { mische } from './Uebung'
import { bereiteReagierenVor } from './Reagieren'


// ─── fehlerMoeglich: alle Fehler, die in einen Zug passen ─────
//
// zug: ein Eintrag aus baueUebung().zuege
// arten: welche Fehlerarten, z.B. ['ersatz'] oder ['ersatz', 'vertauscht']
//
// zurueck: [ {
//   art,
//   teile:  [ {id, text, ...}, ... ],  // der Spruch MIT dem Fehler
//   falsch: [ id, ... ],               // die Bausteine, die den Fehler machen
//   regeln: [ satz, ... ],             // die verletzte(n) Regel(n)
//   richtig: [ text, ... ],            // was an ihrer Stelle stehen muesste
//   beruehrt: [ satz, ... ]            // jede Regel, die der Fehler streift —
//                                      // beim Ersatz auch die des fehlenden
//                                      // Bausteins. Taugt nicht als falsche
//                                      // Antwort bei "Regel benennen".
// }, ... ]

export function fehlerMoeglich(zug, arten) {
  const moeglich = []

  if (arten.includes('ersatz')) {
    for (const a of zug.ablenker) {
      if (!a.ersetzt) continue
      zug.teile.forEach((t, i) => {
        if (t.rolle !== a.ersetzt || t.text === a.text) return
        const teile = [...zug.teile]
        teile[i] = a
        moeglich.push({
          art: 'ersatz', teile, falsch: [a.id], regeln: [a.regel], richtig: [t.text],
          beruehrt: [a.regel, t.regel]
        })
      })
    }
  }

  if (arten.includes('vertauscht')) {
    for (let i = 0; i < zug.teile.length - 1; i++) {
      const [x, y] = [zug.teile[i], zug.teile[i + 1]]
      if (x.text === y.text) continue
      const teile = [...zug.teile]
      teile[i] = y
      teile[i + 1] = x
      moeglich.push({
        art: 'vertauscht', teile, falsch: [x.id, y.id],
        regeln: [...new Set([x.regel, y.regel])],
        richtig: [x.text, y.text],
        beruehrt: [x.regel, y.regel]
      })
    }
  }

  return moeglich
}


// ─── fehlerhafteZuege: welche Zuege bekommen einen Fehler? ────
//
// Waehlt zufaellig "anzahl" Zuege, in die mindestens ein Fehler der
// gewuenschten Art passt, und baut in jeden genau EINEN ein.
//
// zurueck: { [zug.nr]: Fehler }  (ein Eintrag aus fehlerMoeglich)
// Zuege ohne Eintrag bleiben richtig. Ein schlichtes Objekt, keine Map: es
// wird in der Sitzung als JSON gespeichert.

function fehlerhafteZuege(uebung, arten, anzahl) {
  const kandidaten = mische(uebung.zuege.filter(z => fehlerMoeglich(z, arten).length > 0))
  const fehler = {}
  for (const z of kandidaten.slice(0, anzahl)) {
    fehler[z.nr] = mische(fehlerMoeglich(z, arten))[0]
  }
  return fehler
}


// ─── zwischen: ganze Zufallszahl von a bis b ──────────────────

function zwischen(a, b) {
  return a + Math.floor(Math.random() * (b - a + 1))
}

const spruch = (teile) => teile.map(t => t.text).join(' ')


// ─── bereiteVor: alles Gewuerfelte einer Uebungsart, einmal ───
//
// Haengt an die Uebung, was die Ansicht der Uebungsart braucht und was
// zufaellig ist: welche Fehler, welche Fassungen, welche Antworten.
// Einmal gewuerfelt und mit der Uebung gespeichert — so zeigt die Sitzung
// nach einem Neuladen dieselben Fehler. Die Ansichten wuerfeln nichts.
//
//   bereiteVor('fehlerMarkieren', baueUebung(schema, inventar))
//   -> { ...uebung, art: 'fehlerMarkieren', fehlersuche: { fehler } }
//
// Unbekannte Art (oder "allesSortieren"): nur "art" kommt dazu.
// "reagieren" geht nach Reagieren.js; rolle gilt nur dort.

const MAX_FEHLERHAFT = 2   // Zuege mit Fehler bei Markieren und Korrigieren
const FALSCHE_FASSUNGEN = 2
const FALSCHE_REGELN = 3

export function bereiteVor(art, uebung, rolle) {
  if (art === 'reagieren') return bereiteReagierenVor(uebung, rolle)

  const z = uebung.zuege.length
  let fehlersuche = null

  if (art === 'fehlerMarkieren') {
    // Nur Ersatz: ein Tausch hat keinen einzelnen falschen Baustein.
    fehlersuche = { fehler: fehlerhafteZuege(uebung, ['ersatz'], zwischen(1, Math.min(MAX_FEHLERHAFT, z))) }
  }

  if (art === 'fehlerKorrigieren') {
    fehlersuche = { fehler: fehlerhafteZuege(uebung, ['ersatz', 'vertauscht'], zwischen(1, Math.min(MAX_FEHLERHAFT, z))) }
  }

  if (art === 'regelBenennen') {
    // Genau ein Zug mit Fehler. Beim Tausch gelten beide Regeln — eine davon
    // steht zur Wahl. Falsche Antworten sind andere Regelsaetze derselben
    // Uebung, ausser denen, die der Fehler streift ("beruehrt").
    const fehler = fehlerhafteZuege(uebung, ['ersatz', 'vertauscht'], 1)
    const fehlerZug = Number(Object.keys(fehler)[0])
    const f = fehler[fehlerZug]
    const antwort = mische(f.regeln)[0]
    const alle = uebung.zuege.flatMap(zug => [...zug.teile, ...zug.ablenker].map(t => t.regel))
    const andere = mische([...new Set(alle)].filter(r => r && !f.beruehrt.includes(r)))
    fehlersuche = {
      fehlerZug,
      fehler: f,
      antwort,
      optionen: mische([antwort, ...andere.slice(0, FALSCHE_REGELN)])
    }
  }

  if (art === 'fassungWaehlen') {
    // Pro Zug die richtige Fassung und zwei falsche. Gleich lautende Fehler
    // (zwei Ablenker koennen denselben Text ergeben) zaehlen nur einmal.
    fehlersuche = {
      fassungen: uebung.zuege.map(zug => {
        const falsche = []
        for (const f of mische(fehlerMoeglich(zug, ['ersatz', 'vertauscht']))) {
          const text = spruch(f.teile)
          if (falsche.length < FALSCHE_FASSUNGEN && !falsche.some(x => x.text === text)) {
            falsche.push({ text, richtig: false, regeln: f.regeln })
          }
        }
        return mische([{ text: spruch(zug.teile), richtig: true, regeln: [] }, ...falsche])
      })
    }
  }

  return { ...uebung, art, fehlersuche }
}
